import axios from 'axios';

const API_BASE_URL = '/api';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Add a request interceptor to include CSRF token
axios.interceptors.request.use(
  (config) => {
    const csrfToken = document.querySelector<HTMLMetaElement>('[name=csrfmiddlewaretoken]')?.content;
    if (csrfToken) {
      config.headers['X-CSRFToken'] = csrfToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      window.location.href = '/admin/login/';
    }
    return Promise.reject(error);
  }
);

export const apiService = {
  // Dashboard API
  getDashboardData: async () => {
    const response = await axios.get('/dashboard/');
    return response.data;
  },

  // Products API
  getProducts: async () => {
    const response = await axios.get('/products/');
    return response.data;
  },

  getProduct: async (id: number) => {
    const response = await axios.get(`/products/${id}/`);
    return response.data;
  },

  // Master Batch Records API
  getMasterBatchRecords: async (params?: any) => {
    const response = await axios.get('/master-batch-records/', { params });
    return response.data;
  },

  getMasterBatchRecord: async (id: number) => {
    const response = await axios.get(`/master-batch-records/${id}/`);
    return response.data;
  },

  getMBRProcessSteps: async (mbrId: number) => {
    const response = await axios.get(`/master-batch-records/${mbrId}/process_steps/`);
    return response.data;
  },

  // Process Steps API
  getProcessSteps: async (params?: any) => {
    const response = await axios.get('/process-steps/', { params });
    return response.data;
  },

  // Lines API
  getLines: async () => {
    const response = await axios.get('/lines/');
    return response.data;
  },

  // Batches API
  getBatches: async (params?: any) => {
    const response = await axios.get('/batches/', { params });
    return response.data;
  },

  getBatch: async (id: number) => {
    const response = await axios.get(`/batches/${id}/`);
    return response.data;
  },

  getBatchExecutionDetails: async (id: string | number) => {
    const response = await axios.get(`/batches/${id}/execution_details/`);
    return response.data;
  },

  getWorkOrders: async (status?: string) => {
    const params = status ? { status } : {};
    const response = await axios.get('/batches/work_orders/', { params });
    return response.data;
  },

  getQAReviewBatches: async () => {
    const response = await axios.get('/batches/qa_review/');
    return response.data;
  },

  approveBatch: async (id: number) => {
    const response = await axios.post(`/batches/${id}/approve/`);
    return response.data;
  },

  rejectBatch: async (id: number, reason: string) => {
    const response = await axios.post(`/batches/${id}/reject/`, { reason });
    return response.data;
  },

  // Batch Steps API
  getBatchSteps: async (params?: any) => {
    const response = await axios.get('/batch-steps/', { params });
    return response.data;
  },

  completeBatchStep: async (id: string | number) => {
    const response = await axios.post(`/batch-steps/${id}/complete/`);
    return response.data;
  },

  skipBatchStep: async (id: string | number, reason: string) => {
    const response = await axios.post(`/batch-steps/${id}/skip/`, { reason });
    return response.data;
  },

  // Batch Parameters API
  getBatchParameters: async (params?: any) => {
    const response = await axios.get('/batch-parameters/', { params });
    return response.data;
  },

  updateBatchParameter: async (id: number, data: any) => {
    const response = await axios.patch(`/batch-parameters/${id}/`, data);
    return response.data;
  },

  // Materials API
  getMaterials: async (params?: any) => {
    const response = await axios.get('/materials/', { params });
    return response.data;
  },

  // Equipment API
  getEquipment: async (params?: any) => {
    const response = await axios.get('/equipment/', { params });
    return response.data;
  },

  // Deviations API
  getDeviations: async (params?: any) => {
    const response = await axios.get('/deviations/', { params });
    return response.data;
  },

  createDeviation: async (data: any) => {
    const response = await axios.post('/deviations/', data);
    return response.data;
  },

  updateDeviation: async (id: number, data: any) => {
    const response = await axios.patch(`/deviations/${id}/`, data);
    return response.data;
  },

  // Audit Trail API
  getAuditTrail: async (params?: any) => {
    const response = await axios.get('/audit-trail/', { params });
    return response.data;
  },

  // Search API
  searchBatches: async (query: string, params?: any) => {
    const searchParams = { search: query, ...params };
    const response = await axios.get('/batches/', { params: searchParams });
    return response.data;
  },
};

export default apiService;